import React, { useRef, useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableFooter, CircularProgress, Typography ,Box} from "@mui/material";

const MONTHLY_COLUMNS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

// Placeholder for external helpers
const formatNumber = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SalesTableGroupMonthMonthly = ({ rows, grandColumnTotals, loading, loadNextPage, hasMore }) => {

    // 1. Determine the primary descriptive key dynamically
    const descriptiveKey = useMemo(() => {
        if (!rows || rows.length === 0) return 'Code'; // Default fallback

        const firstRowKeys = Object.keys(rows[0]);
        
        if (firstRowKeys.includes('Code')) {
            return 'Code';
        }
        if (firstRowKeys.includes('SGROUP')) {
            return 'SGROUP';
        }
        if (firstRowKeys.includes('GROUP')) {
            return 'GROUP';
        }
        if (firstRowKeys.includes('CATEGORY')) {
            return 'CATEGORY';
        }
        
        return 'Code';
    }, [rows]);

    // 2. Define dynamic fixed columns based on the determined key
    const FIXED_COLUMNS = useMemo(() => {
        const firstRow = rows.length > 0 ? rows[0] : {};

        if (descriptiveKey === 'Code') {
            // For Code-based reports, we include Description
            return ['Code', 'Description'];
        }
        
        // For Group-based reports, we include YEAR if the field is present
        if (firstRow.YEAR !== undefined) {
             return [descriptiveKey, 'YEAR'];
        }
        
        // Fallback: just the primary key
        return [descriptiveKey];
    }, [descriptiveKey, rows]);

    const TOTAL_COLUMNS = FIXED_COLUMNS.length + MONTHLY_COLUMNS.length + 1; // Fixed columns + 12 months + 1 total

    const tableContainerRef = useRef(null);
    const handleTableScroll = () => {

        const currentTarget = tableContainerRef.current;

        if (!currentTarget || loading || !hasMore || rows.length === 0) return;

        const { scrollTop, scrollHeight, clientHeight } = currentTarget;

        // Load the next page when reaching the bottom (with a 10px buffer)
        if (scrollHeight - scrollTop <= clientHeight + 10) {
            loadNextPage();
        }
    };


    const renderHeader = () => {
        let leftOffset = 0; // Tracks the cumulative width for sticky columns

        return (
            <TableHead>
                <TableRow>
                    {FIXED_COLUMNS.map((columnKey, index) => {
                        // Sticky only the first two columns (Code and Description, or Group and Year)
                        const isSticky = index < FIXED_COLUMNS.length && index < 2; 
                        const minWidth = columnKey === 'Description' ? 350 : 150; 
                        
                        const stickyStyle = isSticky ? {
                            position: 'sticky',
                            left: leftOffset,
                            zIndex: 10,
                            backgroundColor: '#e0f7fa',
                            borderRight: '1px solid #ccc',
                        } : {};

                        if (isSticky) {
                            // Accumulate width for the next column's left offset
                            leftOffset += minWidth; 
                        }

                        return (
                            <TableCell 
                                key={columnKey} 
                                style={{ 
                                    minWidth: minWidth, 
                                    whiteSpace: columnKey === 'Description' ? 'normal' : 'nowrap',
                                    ...stickyStyle
                                }}
                            >
                                {columnKey.toUpperCase()}
                            </TableCell>
                        );
                    })}

                    {/* Render month columns */}
                    {MONTHLY_COLUMNS.map(month => (
                        <TableCell key={month} align="right" style={{ minWidth: 90, whiteSpace: 'nowrap' }}>{month}</TableCell>
                    ))}
                    {/* Render total column */}
                    <TableCell align="right" style={{ minWidth: 100, whiteSpace: 'nowrap' }}>TOTAL</TableCell>
                </TableRow>
            </TableHead>
        );
    };

    const renderBody = () => {
        return (
            <TableBody>
                {rows
                    // Filter out non-object rows for safety
                    .filter(row => typeof row === 'object' && row !== null && Object.keys(row).length > 0)
                    .map((row, index) => {
                    
                    let leftOffset = 0; // Reset offset for each row

                    return (
                        <TableRow key={`month-row-${index}`} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                            
                            {/* Render fixed data (iterates over Code, Description, or Group, Year) */}
                            {FIXED_COLUMNS.map((columnKey, colIndex) => {
                                // Sticky only the first two columns (Code and Description, or Group and Year)
                                const isSticky = colIndex < FIXED_COLUMNS.length && colIndex < 2;
                                const minWidth = columnKey === 'Description' ? 350 : 150; 

                                const stickyStyle = isSticky ? {
                                    position: 'sticky', 
                                    left: leftOffset, 
                                    zIndex: 9, 
                                    backgroundColor: '#ffffff', 
                                    borderRight: '1px solid #ccc' 
                                } : {};

                                if (isSticky) {
                                    // Accumulate width for the next column's left offset
                                    leftOffset += minWidth; 
                                }

                                return (
                                    <TableCell 
                                        key={columnKey}
                                        style={stickyStyle}
                                    >
                                        {/* Handle null values gracefully, especially for Description */}
                                        {row[columnKey] === null || row[columnKey] === undefined ? 'N/A' : row[columnKey]}
                                    </TableCell>
                                );
                            })}
                            
                            {/* Render monthly data */}
                            {MONTHLY_COLUMNS.map(month => (
                                <TableCell key={month} align="right">{formatNumber(row[month])}</TableCell>
                            ))}
                            
                            {/* Render total data */}
                            <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatNumber(row.TOTAL)}</TableCell>
                        </TableRow>
                    );
                })}

                {/* Load More Indicator - Correctly placed inside TableRow/TableCell */}
                {hasMore && (
                    <TableRow>
                        <TableCell colSpan={TOTAL_COLUMNS} align="center">
                            <Box sx={{ py: 2 }}>
                                <CircularProgress size={24} />
                                <Typography variant="caption" display="block">
                                    Loading more data...
                                </Typography>
                            </Box>
                        </TableCell>
                    </TableRow>
                )}

                {!loading && rows.length === 0 && (
                    <TableRow><TableCell colSpan={TOTAL_COLUMNS} align="center"><Typography variant="subtitle1" color="textSecondary">No monthly data found.</Typography></TableCell></TableRow>
                )}
            </TableBody>
        );
    };

    const renderFooter = () => {
        // Grand totals only apply to month columns and TOTAL
        const totalKeys = [...MONTHLY_COLUMNS, 'TOTAL'];
        // Descriptive span is based on the dynamically determined number of fixed columns
        const descriptiveSpan = FIXED_COLUMNS.length; 

        return (
            <TableFooter>
                <TableRow sx={{ backgroundColor: '#b3b3b3', borderTop: '3px double #000' }}>
                    <TableCell colSpan={descriptiveSpan} align="right"
                        sx={{ fontWeight: 'bold', fontSize: '1.1em', padding: '8px 16px', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3', borderRight: '3px double #000' }}>
                        GRAND TOTAL:
                    </TableCell>
                    {totalKeys.map(key => (
                        <TableCell key={key} align="right"
                            sx={{ fontWeight: 'bold', fontSize: '1.1em', position: 'sticky', bottom: 0, zIndex: 10, backgroundColor: '#b3b3b3' }}>
                            {formatNumber(grandColumnTotals[key] || 0)}
                        </TableCell>
                    ))}
                </TableRow>
            </TableFooter>
        );
    };

    return (
        <TableContainer
            ref={tableContainerRef}
            onScroll={handleTableScroll} 
            sx={{
                maxHeight: 'calc(100vh - 350px)',
                overflowY: 'auto', width: '100%'
            }}
        >
            <Table stickyHeader aria-label="monthly sales report">
                {renderHeader()}
                {renderBody()}
                {grandColumnTotals && Object.keys(grandColumnTotals).length > 0 && renderFooter()}
            </Table>

            {loading && (
                <div style={{ display: "flex", justifyContent: 'center', alignItems: "center", width: "100%", height: "100%", position: "absolute", zIndex: 100, backgroundColor: "#ffffff80", top: "0px", left: "0px" }}>
                    <CircularProgress size={48} />
                </div>
            )}

        </TableContainer>
    );
};

export default SalesTableGroupMonthMonthly;